import { create } from 'zustand';
import ServiceClassificacaoAnalise, {
  criarServiceClassificacaoAnalise,
} from '@/services/ServiceClassificacaoAnalise';
import { notify } from '@/stores/useToast';

export interface LinhaClassificacaoAnalise {
  [k: string]: string; // satisfaz o constraint Record<string, unknown> do AgnesDataTable
  codigo: string;
  ocorrencia: string;
  diagnostico: string;
  grauRisco: string;
  situacao: string;
  alteradoPor: string;
  atualizacao: string;
  observacoes: string;
}

// campos do Filtro (espelha o contrato da versão antiga: grau de risco + alterado por + data).
export type FiltrosClassificacaoAnalise = {
  grauRisco: string;
  alteradoPor: string;
  atualizacao: string;
};

export interface OpcoesFiltro {
  grauRisco: string[];
  alteradoPor: string[];
}

interface State {
  lista: LinhaClassificacaoAnalise[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  busca: string;
  filtros: FiltrosClassificacaoAnalise;
  opcoes: OpcoesFiltro;
  carregando: boolean;
  exportando: boolean;
  setBusca: (v: string) => void;
  buscar: () => Promise<void>;
  setPage: (p: number) => Promise<void>;
  setSize: (s: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  aplicarFiltros: (f: FiltrosClassificacaoAnalise) => Promise<void>;
  carregarOpcoes: () => Promise<void>;
  exportar: () => Promise<void>;
}

const SEM_FILTRO: FiltrosClassificacaoAnalise = {
  grauRisco: '',
  alteradoPor: '',
  atualizacao: '',
};

const OPCOES_VAZIAS: OpcoesFiltro = {
  grauRisco: [],
  alteradoPor: [],
};

// term + só os filtros preenchidos (o backend faz match exato por coluna; Atualização = data dd/mm/aaaa).
function payload(busca: string, filtros: FiltrosClassificacaoAnalise) {
  const ativos = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v));
  return { term: busca, ...ativos };
}

type Service = ReturnType<typeof criarServiceClassificacaoAnalise>;

// fábrica: cada domínio (Rubricas, CBO, Horários) tem seu store com o service da própria base e
// o nome do arquivo de export. A lógica (busca/paginação/filtro/export) é a mesma.
export function criarStoreClassificacaoAnalise(service: Service, arquivoExport: string) {
  return create<State>((set, get) => ({
    lista: [],
    total: 0,
    page: 0,
    size: 50,
    sortBy: '',
    sortOrder: 'asc',
    busca: '',
    filtros: SEM_FILTRO,
    opcoes: OPCOES_VAZIAS,
    carregando: false,
    exportando: false,

    setBusca(v) {
      set({ busca: v });
    },

    async buscar() {
      const { busca, filtros, page, size, sortBy, sortOrder } = get();
      set({ carregando: true });
      try {
        const sort = sortBy ? `${sortBy},${sortOrder}` : undefined;
        const { data } = await service.search(payload(busca, filtros), { page, size, sort });
        set({ lista: data.content ?? [], total: data.totalElements ?? 0 });
      } catch {
        set({ lista: [], total: 0 });
        notify.error('Erro ao carregar a classificação de análise.');
      } finally {
        set({ carregando: false });
      }
    },

    async setPage(p) {
      set({ page: p });
      await get().buscar();
    },
    async setSize(s) {
      set({ size: s, page: 0 });
      await get().buscar();
    },
    async setSort(by, order) {
      set({ sortBy: by, sortOrder: order, page: 0 });
      await get().buscar();
    },

    async aplicarFiltros(f) {
      set({ filtros: f, page: 0 });
      await get().buscar();
    },

    async carregarOpcoes() {
      try {
        const { data } = await service.filters();
        const op: OpcoesFiltro = { ...OPCOES_VAZIAS };
        if (Array.isArray(data?.grauRisco)) op.grauRisco = data.grauRisco;
        if (Array.isArray(data?.alteradoPor)) op.alteradoPor = data.alteradoPor;
        set({ opcoes: op });
      } catch {
        set({ opcoes: OPCOES_VAZIAS });
      }
    },

    async exportar() {
      set({ exportando: true });
      try {
        const { busca, filtros } = get();
        const { data } = await service.exportData(payload(busca, filtros));
        const url = URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        a.download = arquivoExport;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        notify.error('Não foi possível exportar.');
      } finally {
        set({ exportando: false });
      }
    },
  }));
}

// Rubricas usa o service default (mockável nos testes); CBO e Horários têm bases próprias.
export const useClassificacaoAnaliseStore = criarStoreClassificacaoAnalise(
  ServiceClassificacaoAnalise,
  'classificacao-de-analise.xlsx',
);
export const useCboClassificacaoAnaliseStore = criarStoreClassificacaoAnalise(
  criarServiceClassificacaoAnalise('configuracoes/cbo/classificacao-analise'),
  'cbo-classificacao-de-analise.xlsx',
);
export const useHorariosClassificacaoAnaliseStore = criarStoreClassificacaoAnalise(
  criarServiceClassificacaoAnalise('configuracoes/horarios/classificacao-analise'),
  'horarios-classificacao-de-analise.xlsx',
);
